import './style.css';
import {
  addBrandKitMember,
  connectChannel,
  createBrandKit,
  createConversionJob,
  deleteBrandAsset,
  deleteBrandKit,
  deleteChannel,
  deliverVideoNote,
  downloadBrandAsset,
  establishSession,
  getBrandKits,
  getChannels,
  getCurrentUser,
  reportClientError,
  reportConversionPerformance,
  removeBrandKitMember,
  uploadBrandAsset,
} from './api';
import type { BrowserVideoConverter, LoadedBrandAsset } from './converter';
import { formatBytes, inspectAsset } from './media';
import { StageTimer } from './performance';
import {
  alertUser,
  confirmUser,
  initializeTelegram,
  telegramPlatform,
} from './telegram';
import type { AssetKind, BrandKit, Channel, CurrentUser } from './types';

function element<T extends HTMLElement>(id: string): T {
  const result = document.getElementById(id);
  if (!result) {
    throw new Error(`Element #${id} not found`);
  }
  return result as T;
}

const planBadge = element<HTMLSpanElement>('planBadge');
const sourceInput = element<HTMLInputElement>('sourceInput');
const sourceDrop = element<HTMLLabelElement>('sourceDrop');
const editorWorkspace = element<HTMLDivElement>('editorWorkspace');
const sourceVideo = element<HTMLVideoElement>('sourceVideo');
const sourceName = element<HTMLElement>('sourceName');
const sourceMeta = element<HTMLElement>('sourceMeta');
const replaceSource = element<HTMLButtonElement>('replaceSource');
const circlePreview = element<HTMLDivElement>('circlePreview');
const brandLogoPreview = element<HTMLImageElement>('brandLogoPreview');
const brandFrameImage = element<HTMLImageElement>('brandFrameImage');
const brandFrameVideo = element<HTMLVideoElement>('brandFrameVideo');
const brandOutroPreview = element<HTMLVideoElement>('brandOutroPreview');
const outroPreviewButton = element<HTMLButtonElement>('outroPreviewButton');
const brandPreviewStatus = element<HTMLElement>('brandPreviewStatus');
const startRange = element<HTMLInputElement>('startRange');
const endRange = element<HTMLInputElement>('endRange');
const playheadRange = element<HTMLInputElement>('playheadRange');
const timelineSelection = element<HTMLDivElement>('timelineSelection');
const timelinePlayhead = element<HTMLDivElement>('timelinePlayhead');
const playheadOutput = element<HTMLOutputElement>('playheadOutput');
const zoomRange = element<HTMLInputElement>('zoomRange');
const startOutput = element<HTMLOutputElement>('startOutput');
const endOutput = element<HTMLOutputElement>('endOutput');
const durationOutput = element<HTMLOutputElement>('durationOutput');
const zoomOutput = element<HTMLOutputElement>('zoomOutput');
const kitSelect = element<HTMLSelectElement>('kitSelect');
const manageKitSelect = element<HTMLSelectElement>('manageKitSelect');
const manageKitField = element<HTMLLabelElement>('manageKitField');
const logoControls = element<HTMLDivElement>('logoControls');
const logoSize = element<HTMLInputElement>('logoSize');
const targetSelect = element<HTMLSelectElement>('targetSelect');
const convertButton = element<HTMLButtonElement>('convertButton');
const progressBox = element<HTMLDivElement>('progressBox');
const progressText = element<HTMLSpanElement>('progressText');
const progressPercent = element<HTMLElement>('progressPercent');
const progressBar = element<HTMLElement>('progressBar');
const resultBox = element<HTMLDivElement>('resultBox');
const resultTitle = element<HTMLElement>('resultTitle');
const resultText = element<HTMLElement>('resultText');
const downloadResult = element<HTMLAnchorElement>('downloadResult');
const resendResult = element<HTMLButtonElement>('resendResult');
const brandLocked = element<HTMLDivElement>('brandLocked');
const brandManager = element<HTMLDivElement>('brandManager');
const kitCounter = element<HTMLSpanElement>('kitCounter');
const createKitForm = element<HTMLFormElement>('createKitForm');
const newKitName = element<HTMLInputElement>('newKitName');
const kitEditor = element<HTMLDivElement>('kitEditor');
const deleteKitButton = element<HTMLButtonElement>('deleteKitButton');
const kitSharing = element<HTMLElement>('kitSharing');
const kitSharingHelp = element<HTMLElement>('kitSharingHelp');
const memberCounter = element<HTMLElement>('memberCounter');
const shareKitForm = element<HTMLFormElement>('shareKitForm');
const shareTelegramId = element<HTMLInputElement>('shareTelegramId');
const kitMemberList = element<HTMLDivElement>('kitMemberList');
const channelCounter = element<HTMLSpanElement>('channelCounter');
const connectChannelForm = element<HTMLFormElement>('connectChannelForm');
const channelIdentifier = element<HTMLInputElement>('channelIdentifier');
const channelList = element<HTMLDivElement>('channelList');
const toast = element<HTMLDivElement>('toast');
const telegramGate = element<HTMLElement>('telegramGate');

let currentUser: CurrentUser | null = null;
let brandKits: BrandKit[] = [];
let channels: Channel[] = [];
let selectedManageKitId = '';
let sourceFile: File | null = null;
let sourceUrl: string | null = null;
let resultUrl: string | null = null;
let lastConvertedResult: { file: File; duration: number } | null = null;
let sourceDuration = 0;
let cropX = 0.5;
let cropY = 0.5;
let logoX = 0.85;
let logoY = 0.85;
let converter: BrowserVideoConverter | null = null;
let converterPromise: Promise<BrowserVideoConverter> | null = null;
let warmupPromise: Promise<void> | null = null;
let engineWarmupMs: number | null = null;
let toastTimer: number | null = null;
let brandPreviewRequest = 0;
let brandPreviewUrls: string[] = [];
let previewingOutro = false;

function showToast(message: string, timeout = 3500): void {
  toast.textContent = message;
  toast.hidden = false;
  if (toastTimer !== null) {
    window.clearTimeout(toastTimer);
  }
  toastTimer = window.setTimeout(() => {
    toast.hidden = true;
    toastTimer = null;
  }, timeout);
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'string' && error.trim()) {
    return error.trim();
  }
  if (error && typeof error === 'object') {
    for (const key of ['message', 'reason', 'error'] as const) {
      const value = (error as Record<string, unknown>)[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    try {
      const serialized = JSON.stringify(error);
      if (serialized && serialized !== '{}') {
        return serialized.slice(0, 500);
      }
    } catch {
      // Ignore values that cannot be serialized.
    }
  }
  return 'Видеодвижок завершил работу без описания причины.';
}

function formatTime(seconds: number): string {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  const remainder = Math.floor(safe % 60);
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
}

function clamp(value: number, minimum = 0, maximum = 1): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function setProgress(progress: number, stage: string): void {
  const percent = Math.round(clamp(progress) * 100);
  progressBox.hidden = false;
  progressText.textContent = stage;
  progressPercent.textContent = `${percent}%`;
  progressBar.style.width = `${percent}%`;
}

async function getConverter(): Promise<BrowserVideoConverter> {
  if (converter) {
    return converter;
  }
  if (!converterPromise) {
    converterPromise = import('./converter')
      .then(({ BrowserVideoConverter }) => {
        converter = new BrowserVideoConverter();
        return converter;
      })
      .catch((error: unknown) => {
        converterPromise = null;
        throw error;
      });
  }
  return converterPromise;
}

function prewarmConverter(): void {
  if (converter?.ready || warmupPromise) {
    return;
  }
  const startedAt = performance.now();
  warmupPromise = getConverter()
    .then((instance) => instance.warmUp())
    .then(() => {
      engineWarmupMs = Math.max(0, Math.round(performance.now() - startedAt));
    })
    .catch((error: unknown) => {
      warmupPromise = null;
      console.warn('Background FFmpeg warm-up failed', error);
    });
}

interface PreviewGeometry {
  size: number;
  width: number;
  height: number;
  overflowX: number;
  overflowY: number;
}

function previewGeometry(zoom = Number(zoomRange.value) / 100): PreviewGeometry {
  const size = circlePreview.getBoundingClientRect().width;
  const aspect =
    sourceVideo.videoWidth > 0 && sourceVideo.videoHeight > 0
      ? sourceVideo.videoWidth / sourceVideo.videoHeight
      : 1;
  const baseWidth = aspect >= 1 ? size * aspect : size;
  const baseHeight = aspect >= 1 ? size : size / aspect;
  const width = baseWidth * zoom;
  const height = baseHeight * zoom;
  return {
    size,
    width,
    height,
    overflowX: Math.max(0, width - size),
    overflowY: Math.max(0, height - size),
  };
}

function updatePreview(): void {
  const zoom = Number(zoomRange.value) / 100;
  const geometry = previewGeometry(zoom);
  sourceVideo.style.width = `${geometry.width}px`;
  sourceVideo.style.height = `${geometry.height}px`;
  sourceVideo.style.left = `${-geometry.overflowX * cropX}px`;
  sourceVideo.style.top = `${-geometry.overflowY * cropY}px`;
  zoomOutput.value = `${zoom.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}×`;
  updateLogoPreviewGeometry();
}

function setBrandPreviewStatus(message = ''): void {
  brandPreviewStatus.textContent = message;
  brandPreviewStatus.hidden = !message;
}

function stopOutroPreview(): void {
  previewingOutro = false;
  circlePreview.classList.remove('is-previewing-outro');
  brandOutroPreview.pause();
  brandOutroPreview.hidden = true;
  outroPreviewButton.textContent = 'Показать заглушку';
}

function clearBrandPreview(): void {
  stopOutroPreview();
  brandLogoPreview.onload = null;
  brandLogoPreview.onerror = null;
  brandFrameImage.onload = null;
  brandFrameImage.onerror = null;
  brandFrameVideo.oncanplay = null;
  brandFrameVideo.onerror = null;
  brandOutroPreview.onloadedmetadata = null;
  brandOutroPreview.onerror = null;
  brandLogoPreview.hidden = true;
  brandFrameImage.hidden = true;
  brandFrameVideo.hidden = true;
  brandFrameVideo.pause();
  brandOutroPreview.pause();
  brandLogoPreview.removeAttribute('src');
  brandFrameImage.removeAttribute('src');
  brandFrameVideo.removeAttribute('src');
  brandOutroPreview.removeAttribute('src');
  outroPreviewButton.hidden = true;
  for (const url of brandPreviewUrls) {
    URL.revokeObjectURL(url);
  }
  brandPreviewUrls = [];
  setBrandPreviewStatus();
}

function updateLogoPreviewGeometry(): void {
  const size = clamp(Number(logoSize.value), 8, 35);
  brandLogoPreview.style.width = `${size}%`;
  const bounds = circlePreview.getBoundingClientRect();
  if (bounds.width > 0 && bounds.height > 0 && !brandLogoPreview.hidden) {
    const halfWidth = brandLogoPreview.offsetWidth / bounds.width / 2;
    const halfHeight = brandLogoPreview.offsetHeight / bounds.height / 2;
    logoX = clamp(logoX, halfWidth, 1 - halfWidth);
    logoY = clamp(logoY, halfHeight, 1 - halfHeight);
  }
  brandLogoPreview.style.left = `${logoX * 100}%`;
  brandLogoPreview.style.top = `${logoY * 100}%`;
}

async function refreshBrandPreview(): Promise<void> {
  const requestId = ++brandPreviewRequest;
  clearBrandPreview();
  const kit = selectedKit();
  if (!kit) {
    return;
  }
  const entries = Object.entries(kit.assets) as Array<
    [AssetKind, NonNullable<BrandKit['assets'][AssetKind]>]
  >;
  if (!entries.length) {
    return;
  }

  setBrandPreviewStatus('Загружаем оформление…');
  const loaded = await Promise.allSettled(
    entries.map(async ([kind, descriptor]) => ({
      kind,
      descriptor,
      blob: await downloadBrandAsset(descriptor.id),
    })),
  );
  if (requestId !== brandPreviewRequest) {
    return;
  }

  let failures = 0;
  for (const result of loaded) {
    if (result.status === 'rejected') {
      failures += 1;
      continue;
    }
    const { kind, descriptor, blob } = result.value;
    const url = URL.createObjectURL(blob);
    brandPreviewUrls.push(url);

    if (kind === 'logo') {
      brandLogoPreview.onload = () => {
        if (requestId === brandPreviewRequest) {
          brandLogoPreview.hidden = false;
          updateLogoPreviewGeometry();
        }
      };
      brandLogoPreview.onerror = () => {
        brandLogoPreview.hidden = true;
        setBrandPreviewStatus('Браузер не смог показать логотип, но файл сохранён.');
      };
      updateLogoPreviewGeometry();
      brandLogoPreview.src = url;
      continue;
    }

    if (kind === 'frame' && descriptor.mimeType.startsWith('image/')) {
      brandFrameImage.onload = () => {
        if (requestId === brandPreviewRequest) {
          brandFrameImage.hidden = false;
        }
      };
      brandFrameImage.onerror = () => {
        brandFrameImage.hidden = true;
        setBrandPreviewStatus('Браузер не смог показать рамку, но она попадёт в готовый кружок.');
      };
      brandFrameImage.src = url;
      continue;
    }

    if (kind === 'frame') {
      brandFrameVideo.oncanplay = () => {
        if (requestId !== brandPreviewRequest) {
          return;
        }
        brandFrameVideo.hidden = false;
        void brandFrameVideo.play().catch(() => undefined);
      };
      brandFrameVideo.onerror = () => {
        brandFrameVideo.hidden = true;
        setBrandPreviewStatus(
          'Этот MOV не воспроизводится в браузере, но рамка попадёт в готовый кружок.',
        );
      };
      brandFrameVideo.src = url;
      brandFrameVideo.load();
      continue;
    }

    brandOutroPreview.onloadedmetadata = () => {
      if (requestId === brandPreviewRequest) {
        outroPreviewButton.hidden = false;
      }
    };
    brandOutroPreview.onerror = () => {
      outroPreviewButton.hidden = true;
      setBrandPreviewStatus('Браузер не смог воспроизвести заглушку, но файл сохранён.');
    };
    brandOutroPreview.src = url;
    brandOutroPreview.load();
  }

  if (failures) {
    setBrandPreviewStatus('Не удалось загрузить часть оформления для предпросмотра.');
  } else if (!brandPreviewStatus.textContent?.includes('не смог')) {
    setBrandPreviewStatus();
  }
}

type TimelineHandle = 'start' | 'end';

function syncPlayhead(time: number): void {
  if (!sourceDuration) {
    return;
  }
  if (previewingOutro) {
    stopOutroPreview();
  }
  const nextTime = clamp(time, 0, sourceDuration);
  playheadRange.value = String(nextTime);
  playheadRange.setAttribute('aria-valuetext', formatTime(nextTime));
  timelinePlayhead.style.left = `${(nextTime / sourceDuration) * 100}%`;
  playheadOutput.value = `Кадр ${formatTime(nextTime)}`;

  if (Number.isFinite(sourceVideo.duration)) {
    sourceVideo.currentTime = Math.min(nextTime, Math.max(0, sourceVideo.duration - 0.04));
  }
}

function syncTimeControls(active: TimelineHandle = 'start'): void {
  if (!sourceDuration) {
    return;
  }
  const minimumDuration = Math.min(0.25, sourceDuration);
  let start = clamp(Number(startRange.value), 0, sourceDuration);
  let end = clamp(Number(endRange.value), 0, sourceDuration);

  if (end - start < minimumDuration) {
    if (active === 'start') {
      start = Math.max(0, end - minimumDuration);
    } else {
      end = Math.min(sourceDuration, start + minimumDuration);
    }
  }
  if (end - start > 60) {
    if (active === 'start') {
      end = start + 60;
    } else {
      start = end - 60;
    }
  }

  startRange.value = String(start);
  endRange.value = String(end);
  startOutput.value = formatTime(start);
  endOutput.value = formatTime(end);
  durationOutput.value = formatTime(end - start);
  timelineSelection.style.left = `${(start / sourceDuration) * 100}%`;
  timelineSelection.style.width = `${((end - start) / sourceDuration) * 100}%`;

  const previewTime = active === 'end' ? Math.max(start, end - 0.05) : start;
  syncPlayhead(previewTime);
}

function setSource(file: File): Promise<void> {
  const allowedExtension = /\.(mp4|mov|webm|m4v)$/i.test(file.name);
  if (!file.type.startsWith('video/') && !allowedExtension) {
    return Promise.reject(new Error('Выберите видеофайл.'));
  }
  if (!file.size || file.size > 100 * 1024 * 1024) {
    return Promise.reject(new Error('Размер исходного видео должен быть не больше 100 МБ.'));
  }
  if (sourceUrl) {
    URL.revokeObjectURL(sourceUrl);
  }
  sourceFile = file;
  lastConvertedResult = null;
  sourceUrl = URL.createObjectURL(file);

  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error('Видео загружается слишком долго. Попробуйте MP4/H.264.'));
    }, 15_000);
    const cleanup = (): void => {
      window.clearTimeout(timeout);
      sourceVideo.removeEventListener('loadedmetadata', handleLoaded);
      sourceVideo.removeEventListener('error', handleError);
    };
    const handleError = (): void => {
      cleanup();
      reject(new Error('Браузер не смог открыть это видео. Попробуйте MP4/H.264.'));
    };
    const handleLoaded = (): void => {
      cleanup();
      sourceDuration = sourceVideo.duration;
      if (!Number.isFinite(sourceDuration) || sourceDuration <= 0) {
        reject(new Error('Не удалось определить длительность видео.'));
        return;
      }
      sourceDrop.hidden = true;
      editorWorkspace.hidden = false;
      sourceName.textContent = file.name;
      sourceMeta.textContent = `${formatTime(sourceDuration)} · ${sourceVideo.videoWidth}×${sourceVideo.videoHeight} · ${formatBytes(file.size)}`;
      startRange.max = String(sourceDuration);
      endRange.max = String(sourceDuration);
      playheadRange.max = String(sourceDuration);
      startRange.value = '0';
      endRange.value = String(Math.min(60, sourceDuration));
      playheadRange.value = '0';
      zoomRange.value = '100';
      cropX = 0.5;
      cropY = 0.5;
      syncTimeControls('start');
      updatePreview();
      resultBox.hidden = true;
      prewarmConverter();
      resolve();
    };

    sourceVideo.addEventListener('loadedmetadata', handleLoaded);
    sourceVideo.addEventListener('error', handleError);
    sourceVideo.src = sourceUrl ?? '';
    sourceVideo.load();
  });
}

function selectedKit(): BrandKit | undefined {
  return brandKits.find((kit) => kit.id === kitSelect.value);
}

function managedKit(): BrandKit | undefined {
  return brandKits.find(
    (kit) => kit.id === selectedManageKitId && kit.accessRole === 'owner',
  );
}

function refreshKitSharing(kit: BrandKit | undefined): void {
  kitSharing.hidden = !kit;
  kitMemberList.replaceChildren();
  if (!kit || !currentUser) {
    return;
  }
  const limit = currentUser.rules.maxBrandKitMembers;
  memberCounter.textContent = limit > 0 ? `${kit.members.length} / ${limit}` : String(kit.members.length);
  kitSharingHelp.textContent =
    limit > 0
      ? 'Приглашённые пользователи смогут применять этот набор, но не изменять его.'
      : 'Приглашение участников доступно владельцу набора на тарифе TEAM.';
  shareKitForm.hidden = limit <= 0 || kit.members.length >= limit;

  for (const member of kit.members) {
    const row = document.createElement('div');
    row.className = 'channel-item';
    const copy = document.createElement('div');
    const title = document.createElement('strong');
    title.textContent = member.firstName || `Telegram ${member.telegramId}`;
    const identifier = document.createElement('small');
    identifier.textContent = member.username
      ? `@${member.username} · ${member.telegramId}`
      : String(member.telegramId);
    copy.append(title, identifier);
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'danger-link';
    remove.textContent = 'Убрать';
    remove.addEventListener('click', async () => {
      try {
        await removeBrandKitMember(kit.id, member.telegramId);
        brandKits = await getBrandKits();
        refreshKitControls();
      } catch (error) {
        alertUser(errorMessage(error));
      }
    });
    row.append(copy, remove);
    kitMemberList.append(row);
  }
}

function refreshKitControls(): void {
  const previousEditorKit = kitSelect.value;
  const ownedKits = brandKits.filter((kit) => kit.accessRole === 'owner');
  const editorKits = brandKits.filter(
    (kit) => currentUser?.rules.customAssets || kit.accessRole === 'member',
  );
  kitSelect.replaceChildren(new Option('Без оформления', ''));
  for (const kit of editorKits) {
    const label = kit.accessRole === 'member' ? `Общий · ${kit.name}` : kit.name;
    kitSelect.add(new Option(label, kit.id));
  }
  if (editorKits.some((kit) => kit.id === previousEditorKit)) {
    kitSelect.value = previousEditorKit;
  }
  logoControls.hidden = !selectedKit()?.assets.logo;

  kitCounter.textContent = `${ownedKits.length} / ${currentUser?.rules.maxBrandKits ?? 0}`;
  manageKitSelect.replaceChildren();
  for (const kit of ownedKits) {
    manageKitSelect.add(new Option(kit.name, kit.id));
  }
  if (!ownedKits.some((kit) => kit.id === selectedManageKitId)) {
    selectedManageKitId = ownedKits[0]?.id ?? '';
  }
  manageKitSelect.value = selectedManageKitId;
  manageKitField.hidden = !ownedKits.length;
  kitEditor.hidden = !ownedKits.length;
  const canCreate = Boolean(
    currentUser?.rules.customAssets &&
      ownedKits.length < (currentUser?.rules.maxBrandKits ?? 0),
  );
  createKitForm.hidden = !canCreate;

  const kit = managedKit();
  refreshKitSharing(kit);
  for (const kind of ['logo', 'frame', 'outro'] as const) {
    const state = document.querySelector<HTMLElement>(`[data-asset-state="${kind}"]`);
    const deleteButton = document.querySelector<HTMLButtonElement>(`[data-asset-delete="${kind}"]`);
    const asset = kit?.assets[kind];
    if (state) {
      state.textContent = asset ? `${asset.fileName} · ${formatBytes(asset.sizeBytes)}` : 'Не загружен';
    }
    if (deleteButton) {
      deleteButton.hidden = !asset;
    }
  }
  void refreshBrandPreview();
}

function refreshChannelControls(): void {
  channelCounter.textContent = `${channels.length} / ${currentUser?.rules.maxChannels ?? 0}`;
  const previousTarget = targetSelect.value;
  targetSelect.replaceChildren(new Option('Мне в личные сообщения', ''));
  for (const channel of channels) {
    targetSelect.add(new Option(`Канал: ${channel.title}`, channel.id));
  }
  if (channels.some((channel) => channel.id === previousTarget)) {
    targetSelect.value = previousTarget;
  }
  connectChannelForm.hidden =
    !currentUser?.rules.directPublish || channels.length >= (currentUser?.rules.maxChannels ?? 0);
  channelList.replaceChildren();
  for (const channel of channels) {
    const row = document.createElement('div');
    row.className = 'channel-item';
    const copy = document.createElement('div');
    const title = document.createElement('strong');
    title.textContent = channel.title;
    const identifier = document.createElement('small');
    identifier.textContent = channel.username ? `@${channel.username}` : channel.telegramChatId;
    copy.append(title, identifier);
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'danger-link';
    remove.textContent = 'Удалить';
    remove.addEventListener('click', async () => {
      if (!(await confirmUser(`Отключить канал «${channel.title}»?`))) {
        return;
      }
      try {
        await deleteChannel(channel.id);
        channels = await getChannels();
        refreshChannelControls();
      } catch (error) {
        alertUser(errorMessage(error));
      }
    });
    row.append(copy, remove);
    channelList.append(row);
  }
}

function renderUser(): void {
  if (!currentUser) {
    return;
  }
  planBadge.textContent = currentUser.plan.toUpperCase();
  brandLocked.hidden = currentUser.rules.customAssets;
  brandManager.hidden = !currentUser.rules.customAssets;
  refreshKitControls();
  refreshChannelControls();
}

async function reloadProductData(): Promise<void> {
  currentUser = await getCurrentUser();
  [brandKits, channels] = await Promise.all([getBrandKits(), getChannels()]);
  renderUser();
}

async function loadSelectedAssets(): Promise<Partial<Record<AssetKind, LoadedBrandAsset>>> {
  const kit = selectedKit();
  if (!kit) {
    return {};
  }
  const entries = await Promise.all(
    (Object.entries(kit.assets) as Array<[AssetKind, NonNullable<BrandKit['assets'][AssetKind]>]>).map(
      async ([kind, descriptor]) => [
        kind,
        { descriptor, blob: await downloadBrandAsset(descriptor.id) },
      ] as const,
    ),
  );
  return Object.fromEntries(entries) as Partial<Record<AssetKind, LoadedBrandAsset>>;
}

async function runConversion(): Promise<void> {
  if (!sourceFile || !currentUser) {
    alertUser('Сначала выберите видео.');
    return;
  }
  convertButton.disabled = true;
  resultBox.hidden = true;
  let converted: { file: File; duration: number } | null = null;
  let failureStage = 'Подготавливаем бренд-набор';
  let lastProgress = 0;
  let succeeded = false;
  const engineWasWarm = converter?.ready ?? false;
  const timer = new StageTimer();
  const conversionProgress = (progress: number, stage: string): void => {
    failureStage = stage;
    lastProgress = Math.max(lastProgress, progress);
    timer.enter(stage);
    setProgress(lastProgress, stage);
  };
  try {
    sourceVideo.pause();
    failureStage = 'Проверяем лимиты';
    conversionProgress(0.01, failureStage);
    const conversionJobId = await createConversionJob();
    failureStage = 'Подготавливаем бренд-набор';
    const assets = await loadSelectedAssets();
    conversionProgress(0.03, 'Загружаем код редактора');
    const activeConverter = await getConverter();
    converted = await activeConverter.convert({
      source: sourceFile,
      sourceProbe: {
        duration: sourceDuration,
        width: sourceVideo.videoWidth,
        height: sourceVideo.videoHeight,
        hasAudio: true,
      },
      startSeconds: Number(startRange.value),
      durationSeconds: Number(endRange.value) - Number(startRange.value),
      cropX,
      cropY,
      zoom: Number(zoomRange.value) / 100,
      logoX,
      logoY,
      logoSizePercent: Number(logoSize.value),
      assets,
      onProgress: (progress, stage) =>
        conversionProgress(0.05 + progress * 0.84, stage),
    });
    lastConvertedResult = converted;
    if (resultUrl) {
      URL.revokeObjectURL(resultUrl);
    }
    resultUrl = URL.createObjectURL(converted.file);
    downloadResult.href = resultUrl;
    resultBox.hidden = false;
    resultTitle.textContent = 'Кружок создан';
    resultText.textContent = 'Отправляем его в Telegram…';
    conversionProgress(0.93, 'Отправляем в Telegram');
    const delivery = await deliverVideoNote(
      converted.file,
      converted.duration,
      conversionJobId,
      targetSelect.value || undefined,
    );
    conversionProgress(1, 'Готово');
    succeeded = true;
    resultTitle.textContent = 'Готово';
    resultText.textContent =
      delivery.target === 'channel'
        ? 'Кружок опубликован в выбранном канале.'
        : `Кружок отправлен вам в чат. Осталось сегодня: ${delivery.remainingToday}.`;
    currentUser = await getCurrentUser();
    renderUser();
    showToast('Кружок готов и отправлен');
  } catch (error) {
    const message = errorMessage(error);
    setProgress(lastProgress, `Ошибка · ${failureStage}`);
    void reportClientError(failureStage, message).catch(() => undefined);
    if (converted) {
      resultBox.hidden = false;
      resultTitle.textContent = 'Видео создано, но не отправлено';
      resultText.textContent = `${message} Файл можно скачать ниже.`;
    }
    alertUser(`${message}\n\nЭтап: ${failureStage}`);
  } finally {
    const timing = timer.finish();
    void reportConversionPerformance({
      engine: 'ffmpeg-wasm',
      webCodecsAvailable: 'VideoEncoder' in window && 'VideoDecoder' in window,
      success: succeeded,
      totalMs: timing.totalMs,
      stages: timing.stages,
      engineWasWarm,
      engineWarmupMs,
      source: {
        sizeBytes: sourceFile.size,
        durationSeconds: sourceDuration,
        width: sourceVideo.videoWidth,
        height: sourceVideo.videoHeight,
      },
      outputBytes: converted?.file.size ?? null,
      platform: telegramPlatform(),
    }).catch(() => undefined);
    convertButton.disabled = false;
  }
}

async function resendLastResult(): Promise<void> {
  if (!lastConvertedResult || !currentUser) {
    alertUser('Сначала создайте кружок.');
    return;
  }
  resendResult.disabled = true;
  resultText.textContent = 'Повторно отправляем готовый файл без конвертации…';
  try {
    const conversionJobId = await createConversionJob();
    const delivery = await deliverVideoNote(
      lastConvertedResult.file,
      lastConvertedResult.duration,
      conversionJobId,
      targetSelect.value || undefined,
    );
    resultText.textContent =
      delivery.target === 'channel'
        ? 'Кружок повторно опубликован в выбранном канале.'
        : `Кружок повторно отправлен вам в чат. Осталось сегодня: ${delivery.remainingToday}.`;
    currentUser = await getCurrentUser();
    renderUser();
    showToast('Отправлено без повторной обработки');
  } catch (error) {
    alertUser(errorMessage(error));
    resultText.textContent = 'Повторная отправка не удалась. Готовый файл можно скачать ниже.';
  } finally {
    resendResult.disabled = false;
  }
}

function bindTabs(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach((button) => {
    button.addEventListener('click', () => {
      const tab = button.dataset.tab;
      document.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach((item) =>
        item.classList.toggle('is-active', item === button),
      );
      document.querySelectorAll<HTMLElement>('[data-panel]').forEach((panel) =>
        panel.classList.toggle('is-active', panel.dataset.panel === tab),
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  });
}

function bindSourceControls(): void {
  sourceInput.addEventListener('change', async () => {
    const file = sourceInput.files?.[0];
    if (!file) {
      return;
    }
    try {
      showToast('Открываем видео…', 15_000);
      await setSource(file);
      showToast('Видео загружено');
    } catch (error) {
      alertUser(errorMessage(error));
    }
    // Keep the file input populated. Telegram's Android WebView can revoke
    // access to a content-provider file when input.value is cleared, while
    // the editor still needs to read the same File during conversion.
  });
  replaceSource.addEventListener('click', () => sourceInput.click());
  sourceDrop.addEventListener('dragover', (event) => {
    event.preventDefault();
    sourceDrop.classList.add('is-dragging');
  });
  sourceDrop.addEventListener('dragleave', () => sourceDrop.classList.remove('is-dragging'));
  sourceDrop.addEventListener('drop', async (event) => {
    event.preventDefault();
    sourceDrop.classList.remove('is-dragging');
    const file = event.dataTransfer?.files[0];
    if (!file) {
      return;
    }
    try {
      await setSource(file);
    } catch (error) {
      alertUser(errorMessage(error));
    }
  });
  startRange.addEventListener('input', () => syncTimeControls('start'));
  endRange.addEventListener('input', () => syncTimeControls('end'));
  playheadRange.addEventListener('input', () => syncPlayhead(Number(playheadRange.value)));
  zoomRange.addEventListener('input', updatePreview);
  kitSelect.addEventListener('change', refreshKitControls);
  logoSize.addEventListener('input', updateLogoPreviewGeometry);
  outroPreviewButton.addEventListener('click', () => {
    if (previewingOutro) {
      stopOutroPreview();
      return;
    }
    sourceVideo.pause();
    previewingOutro = true;
    circlePreview.classList.add('is-previewing-outro');
    brandOutroPreview.hidden = false;
    brandOutroPreview.currentTime = 0;
    outroPreviewButton.textContent = 'Вернуться к видео';
    void brandOutroPreview.play().catch(() => {
      stopOutroPreview();
      setBrandPreviewStatus('Браузер не смог воспроизвести заглушку.');
    });
  });
  convertButton.addEventListener('click', runConversion);
  resendResult.addEventListener('click', resendLastResult);
}

function bindPreviewDrag(): void {
  const pointers = new Map<number, { x: number; y: number }>();
  let initialCenter = { x: 0, y: 0 };
  let initialDistance = 0;
  let initialZoom = 1;
  let initialCropX = 0.5;
  let initialCropY = 0.5;

  const points = (): Array<{ x: number; y: number }> => [...pointers.values()];
  const center = (values: Array<{ x: number; y: number }>): { x: number; y: number } => {
    const selected = values.slice(0, 2);
    return {
      x: selected.reduce((sum, point) => sum + point.x, 0) / selected.length,
      y: selected.reduce((sum, point) => sum + point.y, 0) / selected.length,
    };
  };
  const distance = (values: Array<{ x: number; y: number }>): number => {
    const first = values[0];
    const second = values[1];
    if (!first || !second) {
      return 0;
    }
    return Math.hypot(first.x - second.x, first.y - second.y);
  };
  const resetGesture = (): void => {
    const values = points();
    if (!values.length) {
      return;
    }
    initialCenter = center(values);
    initialDistance = distance(values);
    initialZoom = Number(zoomRange.value) / 100;
    initialCropX = cropX;
    initialCropY = cropY;
  };

  circlePreview.addEventListener('pointerdown', (event) => {
    if (previewingOutro) {
      return;
    }
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }
    event.preventDefault();
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    try {
      circlePreview.setPointerCapture(event.pointerId);
    } catch {
      // Some embedded WebViews capture touch pointers automatically.
    }
    resetGesture();
  });

  circlePreview.addEventListener('pointermove', (event) => {
    if (!pointers.has(event.pointerId)) {
      return;
    }
    event.preventDefault();
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const values = points();
    const currentCenter = center(values);
    let zoom = initialZoom;
    if (values.length >= 2 && initialDistance > 0) {
      zoom = clamp(initialZoom * (distance(values) / initialDistance), 1, 3);
      zoomRange.value = String(Math.round(zoom * 100));
    }

    const geometry = previewGeometry(zoom);
    const deltaX = currentCenter.x - initialCenter.x;
    const deltaY = currentCenter.y - initialCenter.y;
    cropX =
      geometry.overflowX > 0
        ? clamp(initialCropX - deltaX / geometry.overflowX)
        : 0.5;
    cropY =
      geometry.overflowY > 0
        ? clamp(initialCropY - deltaY / geometry.overflowY)
        : 0.5;
    updatePreview();
  });

  const stopPointer = (event: PointerEvent): void => {
    pointers.delete(event.pointerId);
    if (pointers.size) {
      resetGesture();
    }
  };
  circlePreview.addEventListener('pointerup', stopPointer);
  circlePreview.addEventListener('pointercancel', stopPointer);

  circlePreview.addEventListener(
    'wheel',
    (event) => {
      if (previewingOutro) {
        return;
      }
      event.preventDefault();
      const current = Number(zoomRange.value);
      zoomRange.value = String(clamp(current - Math.sign(event.deltaY) * 10, 100, 300));
      updatePreview();
    },
    { passive: false },
  );
}

function bindLogoDrag(): void {
  let pointerId: number | null = null;
  let startX = 0;
  let startY = 0;
  let initialLogoX = logoX;
  let initialLogoY = logoY;

  brandLogoPreview.addEventListener('pointerdown', (event) => {
    if (previewingOutro || (event.pointerType === 'mouse' && event.button !== 0)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    pointerId = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
    initialLogoX = logoX;
    initialLogoY = logoY;
    try {
      brandLogoPreview.setPointerCapture(event.pointerId);
    } catch {
      // Telegram WebView may capture touch pointers itself.
    }
  });

  brandLogoPreview.addEventListener('pointermove', (event) => {
    if (pointerId !== event.pointerId) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const bounds = circlePreview.getBoundingClientRect();
    if (!bounds.width || !bounds.height) {
      return;
    }
    logoX = initialLogoX + (event.clientX - startX) / bounds.width;
    logoY = initialLogoY + (event.clientY - startY) / bounds.height;
    updateLogoPreviewGeometry();
  });

  const stopDragging = (event: PointerEvent): void => {
    if (pointerId === event.pointerId) {
      event.stopPropagation();
      pointerId = null;
    }
  };
  brandLogoPreview.addEventListener('pointerup', stopDragging);
  brandLogoPreview.addEventListener('pointercancel', stopDragging);
}

function bindBrandManager(): void {
  createKitForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    try {
      selectedManageKitId = await createBrandKit(newKitName.value);
      newKitName.value = '';
      brandKits = await getBrandKits();
      refreshKitControls();
    } catch (error) {
      alertUser(errorMessage(error));
    }
  });
  manageKitSelect.addEventListener('change', () => {
    selectedManageKitId = manageKitSelect.value;
    refreshKitControls();
  });
  shareKitForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    const kit = managedKit();
    const memberId = Number.parseInt(shareTelegramId.value.trim(), 10);
    if (!kit || !Number.isSafeInteger(memberId) || memberId <= 0) {
      alertUser('Укажите корректный Telegram ID участника.');
      return;
    }
    try {
      await addBrandKitMember(kit.id, memberId);
      shareTelegramId.value = '';
      brandKits = await getBrandKits();
      refreshKitControls();
      showToast('Доступ к набору открыт');
    } catch (error) {
      alertUser(errorMessage(error));
    }
  });
  deleteKitButton.addEventListener('click', async () => {
    const kit = managedKit();
    if (!kit || !(await confirmUser(`Удалить бренд-набор «${kit.name}» и все его файлы?`))) {
      return;
    }
    try {
      await deleteBrandKit(kit.id);
      selectedManageKitId = '';
      brandKits = await getBrandKits();
      refreshKitControls();
    } catch (error) {
      alertUser(errorMessage(error));
    }
  });
  document.querySelectorAll<HTMLInputElement>('[data-asset-input]').forEach((input) => {
    input.addEventListener('change', async () => {
      const kit = managedKit();
      const kind = input.dataset.assetInput as AssetKind | undefined;
      const file = input.files?.[0];
      if (!kit || !kind || !file) {
        return;
      }
      try {
        showToast('Проверяем файл…', 10_000);
        const metadata = await inspectAsset(kind, file);
        await uploadBrandAsset(kit.id, kind, file, { ...metadata });
        brandKits = await getBrandKits();
        refreshKitControls();
        showToast('Файл загружен');
      } catch (error) {
        alertUser(errorMessage(error));
      } finally {
        input.value = '';
      }
    });
  });
  document.querySelectorAll<HTMLButtonElement>('[data-asset-delete]').forEach((button) => {
    button.addEventListener('click', async () => {
      const kit = managedKit();
      const kind = button.dataset.assetDelete as AssetKind | undefined;
      if (!kit || !kind || !(await confirmUser('Удалить этот файл из бренд-набора?'))) {
        return;
      }
      try {
        await deleteBrandAsset(kit.id, kind);
        brandKits = await getBrandKits();
        refreshKitControls();
      } catch (error) {
        alertUser(errorMessage(error));
      }
    });
  });
}

function bindChannels(): void {
  connectChannelForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    try {
      await connectChannel(channelIdentifier.value.trim());
      channelIdentifier.value = '';
      channels = await getChannels();
      refreshChannelControls();
      showToast('Канал подключён');
    } catch (error) {
      alertUser(errorMessage(error));
    }
  });
}

async function start(): Promise<void> {
  await initializeTelegram();
  let authenticated = false;
  try {
    authenticated = await establishSession();
  } catch (error) {
    planBadge.textContent = 'ОШИБКА';
    alertUser(errorMessage(error));
    return;
  }
  if (!authenticated) {
    document.body.classList.add('telegram-required');
    telegramGate.hidden = false;
    return;
  }
  bindTabs();
  bindSourceControls();
  bindPreviewDrag();
  bindLogoDrag();
  bindBrandManager();
  bindChannels();
  try {
    await reloadProductData();
    window.dispatchEvent(new Event('krug:session-ready'));
  } catch (error) {
    planBadge.textContent = 'ОШИБКА';
    alertUser(errorMessage(error));
  }
}

window.addEventListener('resize', updatePreview);
window.visualViewport?.addEventListener('resize', updatePreview);

window.addEventListener('beforeunload', () => {
  brandPreviewRequest += 1;
  clearBrandPreview();
  if (sourceUrl) {
    URL.revokeObjectURL(sourceUrl);
  }
  if (resultUrl) {
    URL.revokeObjectURL(resultUrl);
  }
});

void start();

window.addEventListener('krug:billing-updated', () => {
  if (currentUser) void reloadProductData().catch((error) => showToast(errorMessage(error)));
});
